import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import Reveal from "./Reveal";
import { Loader2, Lock, MapPin, Route, Car, ShieldCheck, Star } from "lucide-react";

const inputCls =
  "w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-ksy-ink placeholder-slate-400 focus:border-ksy-blue focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors";
const labelCls = "block text-sm font-semibold text-slate-700 mb-1.5";

const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function BookAndPay() {
  const { t } = useLang();
  const { user } = useAuth();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    origin: "", destination: "", num_cars: 1, transport_type: "open",
    pickup_date: "", max_delivery_days: 7,
    full_name: "", email: "", phone: "", pickup_address: "", delivery_address: "",
  });
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [paying, setPaying] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/pricing/cities").then((r) => setCities(Array.isArray(r.data?.cities) ? r.data.cities : [])).catch(() => setCities([]));
  }, []);

  useEffect(() => {
    if (user && user.role === "client") {
      setForm((f) => ({ ...f, full_name: f.full_name || user.name || "", email: user.email || "" }));
    }
  }, [user]);

  const canEstimate = form.origin && form.destination && form.origin !== form.destination;

  useEffect(() => {
    if (!canEstimate) { setEstimate(null); return; }
    setEstimating(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/pricing/estimate", {
          origin: form.origin, destination: form.destination,
          num_cars: Number(form.num_cars), transport_type: form.transport_type,
        });
        setEstimate(data);
      } catch (e) { setEstimate(null); }
      finally { setEstimating(false); }
    }, 350);
    return () => clearTimeout(id);
  }, [form.origin, form.destination, form.num_cars, form.transport_type, canEstimate]);

  const contactOk = form.full_name && form.email && form.phone;

  const pay = async () => {
    if (!estimate) return;
    if (!contactOk) { toast.error(t("book.fill_contact")); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        ...form, num_cars: Number(form.num_cars),
        max_delivery_days: Number(form.max_delivery_days) || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
      setPaying(false);
    }
  };

  const cityOptions = useMemo(
    () => (Array.isArray(cities) ? cities : []).map((c) => <option key={c} value={c}>{c}</option>),
    [cities]
  );

  return (
    <section id="book" className="py-20 md:py-28 bg-white" data-testid="book-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="max-w-2xl">
          <span className="text-sm font-bold uppercase tracking-[0.18em] text-ksy-blue">{t("book.label")}</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-ksy-navy tracking-tight">{t("book.title")}</h2>
          <p className="mt-3 text-lg text-slate-500">{t("book.subtitle")}</p>
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-5 gap-6 lg:gap-8 items-start">
          {/* form */}
          <Reveal className="lg:col-span-3">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 grid sm:grid-cols-2 gap-4" data-testid="book-form">
              <div>
                <label className={labelCls}>{t("book.origin")}</label>
                <select value={form.origin} onChange={set("origin")} className={inputCls} data-testid="book-origin">
                  <option value="">{t("book.select_city")}</option>{cityOptions}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("book.destination")}</label>
                <select value={form.destination} onChange={set("destination")} className={inputCls} data-testid="book-destination">
                  <option value="">{t("book.select_city")}</option>{cityOptions}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("book.cars")}</label>
                <input type="number" min="1" max="12" value={form.num_cars} onChange={set("num_cars")} className={inputCls} data-testid="book-cars" />
              </div>
              <div>
                <label className={labelCls}>{t("book.type")}</label>
                <select value={form.transport_type} onChange={set("transport_type")} className={inputCls} data-testid="book-type">
                  <option value="open">{t("book.open")}</option>
                  <option value="enclosed">{t("book.enclosed")}</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("book.pickup_date")}</label>
                <input type="date" value={form.pickup_date} onChange={set("pickup_date")} className={inputCls} data-testid="book-date" />
              </div>
              <div>
                <label className={labelCls}>{t("book.max_days")}</label>
                <input type="number" min="1" max="30" value={form.max_delivery_days} onChange={set("max_delivery_days")} className={inputCls} data-testid="book-maxdays" />
              </div>
              <div className="sm:col-span-2 border-t border-slate-200 pt-4 grid sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>{t("book.name")}</label><input value={form.full_name} onChange={set("full_name")} className={inputCls} data-testid="book-name" /></div>
                <div><label className={labelCls}>{t("book.email")}</label><input type="email" value={form.email} onChange={set("email")} readOnly={!!(user && user.role === "client")} className={`${inputCls} ${user && user.role === "client" ? "bg-slate-100 cursor-not-allowed" : ""}`} data-testid="book-email" /></div>
                <div><label className={labelCls}>{t("book.phone")}</label><input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="book-phone" /></div>
                <div><label className={labelCls}>{t("book.pickup_addr")}</label><input value={form.pickup_address} onChange={set("pickup_address")} className={inputCls} data-testid="book-pickup-addr" /></div>
                <div className="sm:col-span-2"><label className={labelCls}>{t("book.delivery_addr")}</label><input value={form.delivery_address} onChange={set("delivery_address")} className={inputCls} data-testid="book-delivery-addr" /></div>
              </div>
            </div>
          </Reveal>

          {/* summary */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="sticky top-24 bg-ksy-navy rounded-2xl p-6 md:p-8 text-white" data-testid="book-summary">
              <div className="flex items-center gap-2 text-blue-200 text-sm font-bold uppercase tracking-wider mb-5">
                <Route className="w-4 h-4" /> {t("book.summary")}
              </div>

              {!estimate ? (
                <div className="text-blue-100/70 py-8 text-center">
                  {estimating ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : t("book.choose_route")}
                </div>
              ) : (
                <div data-testid="book-summary-content">
                  <div className="flex items-center gap-2 text-sm text-blue-100/80 mb-4">
                    <MapPin className="w-4 h-4 text-blue-300" />
                    <span className="font-semibold text-white">{estimate.origin}</span> → <span className="font-semibold text-white">{estimate.destination}</span>
                  </div>
                  {estimate.famous_route && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-blue-500/20 text-blue-200 rounded-full px-3 py-1 mb-4">
                      <Star className="w-3.5 h-3.5" /> {t("book.famous")}
                    </span>
                  )}
                  <div className="space-y-3 border-t border-white/10 pt-4">
                    <Row icon={Route} label={t("book.distance")} value={`${estimate.miles.toLocaleString()} ${t("book.miles")}`} />
                    <Row icon={Car} label={t("book.per_car")} value={money(estimate.price_per_car)} />
                    <Row icon={Car} label={t("book.vehicles")} value={`× ${estimate.num_cars}`} />
                  </div>
                  <div className="flex items-end justify-between border-t border-white/10 mt-4 pt-4">
                    <span className="text-blue-100/80">{t("book.total")}</span>
                    <span className="font-display font-extrabold text-4xl text-white" data-testid="book-total">{money(estimate.total)}</span>
                  </div>

                  <button
                    onClick={pay}
                    disabled={paying}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-white text-ksy-navy font-bold px-6 py-3.5 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-60"
                    data-testid="book-pay-button"
                  >
                    {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                    {paying ? t("book.processing") : t("book.pay")}
                  </button>
                  <div className="mt-3 flex items-center gap-2 text-xs text-blue-100/60">
                    <ShieldCheck className="w-4 h-4 text-green-400" /> {t("book.test_note")}
                  </div>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-blue-100/70"><Icon className="w-4 h-4" /> {label}</span>
      <span className="font-semibold text-white">{value}</span>
    </div>
  );
}
